/**
 * useLayoutStore — sidebar, aside, breakpoints, form-screen, mobile menu.
 *
 * Part of the useDisplayStore split (architecture review item 5). Owns the
 * visual-layout flags consumed by `MainLayout`, `Sidebar`, `Aside`, and the
 * responsive controller. Theme/chrome state lives in `useChromeStore`;
 * dashboard chart synchronisation lives in `useChartSyncStore`.
 */

import { SIDEBAR_COLLAPSED_KEY as SIDEBAR_KEY } from '@/storage/keys';
import { defineStore } from 'pinia';

function readSidebarCollapsed() {
  if (typeof localStorage === 'undefined') return false;
  const legacy = localStorage.getItem('sidebarCollapsed');
  if (legacy !== null) {
    if (localStorage.getItem(SIDEBAR_KEY) === null) {
      localStorage.setItem(SIDEBAR_KEY, legacy);
    }
    localStorage.removeItem('sidebarCollapsed');
  }
  return localStorage.getItem(SIDEBAR_KEY) === 'true';
}

export const useLayoutStore = defineStore('layout', {
  state: () => ({
    isFormScreen: false,
    isMobileMenuVisible: false,
    isSidebarOpen: true,
    isSidebarCollapsed: false,
    /* Aside */
    isAsideVisible: true,
    isAsideCompact: false,
    isAsideMobileExpanded: false,
    isAsideRightActive: false,
    secondaryMenu: null,
    isAsideHiddenLayout: false,
    isTopVisible: true,
    /* Layout shell */
    isBoxedLayout: false,
    /* Breakpoints */
    isXl: false,
    isLg: false,
    isMd: false,
    isSm: false,
  }),

  actions: {
    setSidebarOpen(value) {
      this.isSidebarOpen = value;
    },

    setSidebarCollapsed(value) {
      this.isSidebarCollapsed = value;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(SIDEBAR_KEY, String(value));
      }
    },

    layoutBreakpoints({ isXl, isLg, isMd, isSm }) {
      this.isXl = isXl;
      this.isLg = isLg;
      this.isMd = isMd;
      this.isSm = isSm;
    },

    mobileMenuToggle(payload = null) {
      this.isMobileMenuVisible = payload !== null ? payload : !this.isMobileMenuVisible;
    },

    asideCompactToggle(payload = null) {
      const isShow = payload !== null ? payload : !this.isAsideCompact;
      if (typeof document !== 'undefined') {
        document.documentElement.classList[isShow ? 'add' : 'remove']('aside-compact');
      }
      this.isAsideCompact = isShow;
    },

    asideMobileToggle(payload = null) {
      const isShow = payload !== null ? payload : !this.isAsideMobileExpanded;
      if (typeof document !== 'undefined') {
        document.documentElement.classList[isShow ? 'add' : 'remove'](
          'm-clipped',
          'mobile-aside-active'
        );
      }
      this.isAsideMobileExpanded = isShow;
    },

    asideRightToggle(payload = null) {
      this.isAsideRightActive = payload !== null ? payload : !this.isAsideRightActive;
    },

    secondaryMenuToggle(payload) {
      this.secondaryMenu = payload;
    },

    asideHiddenLayoutToggle(payload = null) {
      const isHide = payload !== null ? payload : !this.isAsideHiddenLayout;
      if (typeof document !== 'undefined') {
        document.documentElement.classList[isHide ? 'add' : 'remove']('aside-none');
      }
      this.isAsideHiddenLayout = isHide;
      this.asideVisibilityToggle(!isHide);
    },

    asideVisibilityToggle(payload = null) {
      this.isAsideVisible = payload !== null ? payload : !this.isAsideVisible;
    },

    formScreenToggle(payload) {
      this.isFormScreen = payload;
      if (!this.isAsideHiddenLayout) {
        this.isAsideVisible = !payload;
      }
      if (typeof document !== 'undefined') {
        document.documentElement.classList[payload ? 'add' : 'remove']('form-screen');
      }
    },

    responsiveLayoutControl() {
      if (typeof window === 'undefined') return;
      const isXl = window.innerWidth >= 1280;
      const isLg = window.innerWidth >= 1024;
      const isMd = window.innerWidth >= 768;
      const isSm = window.innerWidth < 768;
      this.layoutBreakpoints({ isXl, isLg, isMd, isSm });
      if (isXl) {
        this.asideCompactToggle(true);
        this.asideHiddenLayoutToggle(false);
      } else if (isLg) {
        this.asideCompactToggle(false);
        this.asideHiddenLayoutToggle(true);
      } else {
        this.asideHiddenLayoutToggle(false);
      }
    },

    defaultSidebarCollapsed() {
      this.setSidebarCollapsed(readSidebarCollapsed());
    },
  },
});
